import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { AssetBinaryImage, AssetBinaryVideo } from '@/components/domain/AssetBinaryPreview';
import { downloadAssetContentFile, getAsset } from '@/services/assets';
import { formatRelativeDate } from '@/utils/date';
import type { Asset } from '@/types/api';

/**
 * Asset detail page: single asset view (M3 layout and primitives).
 */
export default function AssetDetailPage() {
  const orgId = useOrgId();
  const { assetId: assetIdParam } = useParams();
  const navigate = useNavigate();
  const assetId = assetIdParam != null ? parseInt(assetIdParam, 10) : NaN;

  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  useEffect(() => {
    if (Number.isNaN(assetId)) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError('');

    getAsset(assetId)
      .then(setAsset)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load asset'))
      .finally(() => setLoading(false));
  }, [assetId]);

  if (Number.isNaN(assetId)) {
    return (
      <div className="aspectly:p-8 aspectly:md:p-12">
        <p className="aspectly:text-error aspectly:font-bold">Invalid asset ID.</p>
        <Link
          to={orgUiPath(orgId, '/assets')}
          className="aspectly:inline-flex aspectly:items-center aspectly:gap-1 aspectly:mt-4 aspectly:text-primary aspectly:font-bold aspectly:hover:underline"
        >
          <span className="material-symbols-outlined aspectly:text-sm">arrow_back</span>
          Back to Assets
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="aspectly:flex aspectly:h-full aspectly:min-h-[50vh] aspectly:items-center aspectly:justify-center">
        <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-4xl aspectly:text-primary">
          progress_activity
        </span>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="aspectly:p-8 aspectly:md:p-12 aspectly:max-w-5xl aspectly:mx-auto aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:min-h-[40vh]">
        <span className="material-symbols-outlined aspectly:text-5xl aspectly:text-error/30 aspectly:mb-4">broken_image</span>
        <p className="aspectly:text-on-surface-variant">{error || 'Asset not found.'}</p>
        <Link
          to={orgUiPath(orgId, '/assets')}
          className="aspectly:inline-flex aspectly:items-center aspectly:gap-1 aspectly:mt-6 aspectly:text-primary aspectly:font-bold aspectly:hover:underline"
        >
          <span className="material-symbols-outlined aspectly:text-sm">arrow_back</span>
          Back to Assets
        </Link>
      </div>
    );
  }

  return (
    <div className="aspectly:w-full aspectly:max-w-5xl aspectly:mx-auto aspectly:p-4 aspectly:md:p-8 aspectly:lg:p-12 aspectly:space-y-8">
      {/* Back nav */}
      <button
        type="button"
        onClick={() => navigate(orgUiPath(orgId, '/assets'))}
        className="aspectly:flex aspectly:items-center aspectly:gap-1.5 aspectly:text-on-surface-variant aspectly:hover:text-primary aspectly:transition-colors aspectly:font-bold aspectly:text-sm aspectly:cursor-pointer"
      >
        <span className="material-symbols-outlined aspectly:text-[18px]">arrow_back</span>
        Assets
      </button>

      {/* Header */}
      <div className="aspectly:space-y-2">
        <h1 className="aspectly:text-3xl aspectly:font-headline aspectly:font-black aspectly:text-on-surface aspectly:truncate">{asset.name}</h1>
        <div className="aspectly:flex aspectly:flex-wrap aspectly:items-center aspectly:gap-3 aspectly:text-sm aspectly:text-on-surface-variant aspectly:font-medium">
          <span className="aspectly:px-2 aspectly:py-0.5 aspectly:rounded-full aspectly:bg-surface-container-high aspectly:text-on-surface aspectly:border aspectly:border-outline-variant/10 aspectly:text-xs aspectly:font-black aspectly:uppercase aspectly:tracking-wider">
            {asset.asset_type === 'video' ? 'Video' : 'Image'}
          </span>
          <span>Modified {formatRelativeDate(asset.updated_at)}</span>
        </div>
        {asset.alt_text && (
          <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:mt-2 aspectly:border-l-2 aspectly:border-primary/30 aspectly:pl-3">
            {asset.alt_text}
          </p>
        )}
      </div>

      {/* Preview Container */}
      <div className="aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:border aspectly:border-outline-variant/10 aspectly:ambient-shadow aspectly:overflow-hidden aspectly:flex aspectly:flex-col aspectly:min-h-[400px]">
        {/* Top bar */}
        <div className="aspectly:px-4 aspectly:py-3 aspectly:bg-surface-container-low aspectly:border-b aspectly:border-outline-variant/10 aspectly:flex aspectly:justify-between aspectly:items-center aspectly:text-xs aspectly:font-bold aspectly:text-on-surface-variant">
          <span className="aspectly:uppercase aspectly:tracking-wider">Preview window</span>
          <button
            type="button"
            disabled={downloading || asset.id == null}
            onClick={() => {
              if (asset.id == null) return;
              setDownloadError('');
              setDownloading(true);
              void downloadAssetContentFile(asset.id, asset.name)
                .catch((err: unknown) =>
                  setDownloadError(err instanceof Error ? err.message : 'Download failed'),
                )
                .finally(() => setDownloading(false));
            }}
            className="aspectly:flex aspectly:items-center aspectly:gap-1.5 aspectly:text-primary aspectly:hover:text-primary-dim aspectly:transition-colors aspectly:disabled:opacity-50 aspectly:disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined aspectly:text-[16px]">download</span>
            {downloading ? 'Downloading…' : 'Download Original'}
          </button>
        </div>
        {downloadError && (
          <div className="aspectly:px-4 aspectly:py-2 aspectly:text-xs aspectly:bg-error-container aspectly:text-on-error-container">
            {downloadError}
          </div>
        )}

        {/* Content area */}
        <div className="aspectly:flex-1 aspectly:p-8 aspectly:bg-surface-container-low/20 aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center">
          <div className="aspectly:max-w-full aspectly:max-h-[65vh] aspectly:rounded-lg aspectly:overflow-hidden aspectly:shadow-xs aspectly:border aspectly:border-outline-variant/5">
            {asset.id == null ? (
              <div className="aspectly:w-[400px] aspectly:h-[300px] aspectly:max-w-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface-container-low aspectly:text-on-surface-variant">
                Asset preview unavailable
              </div>
            ) : asset.asset_type === 'image' ? (
              <AssetBinaryImage
                assetId={asset.id}
                endpoint="content"
                remoteUrlHint={asset.uri}
                alt={asset.alt_text || asset.name}
                className="aspectly:w-auto aspectly:h-auto aspectly:max-w-full aspectly:max-h-[65vh] aspectly:object-contain"
                loadingClassName="aspectly:w-[400px] aspectly:h-[300px] aspectly:max-w-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface-container-low"
              />
            ) : (
              <AssetBinaryVideo
                assetId={asset.id}
                uriHint={asset.uri}
                thumbnailUrlHint={asset.thumbnail_url}
                className="aspectly:w-auto aspectly:h-auto aspectly:max-w-full aspectly:max-h-[65vh] aspectly:object-contain"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
